import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Building2, User, Landmark, Save, MapPin, Clock, Share2 } from 'lucide-react';
import LocationPicker from '@/components/LocationPicker';
import { toast } from 'sonner';
import { apiErrorMessage } from '@/lib/api-error';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

const getDays = (t: TFunction) => [
  { key: 'mon', label: t('adm_merchantCreate.day_mon') },
  { key: 'tue', label: t('adm_merchantCreate.day_tue') },
  { key: 'wed', label: t('adm_merchantCreate.day_wed') },
  { key: 'thu', label: t('adm_merchantCreate.day_thu') },
  { key: 'fri', label: t('adm_merchantCreate.day_fri') },
  { key: 'sat', label: t('adm_merchantCreate.day_sat') },
  { key: 'sun', label: t('adm_merchantCreate.day_sun') },
];

export default function AdminMerchantCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: '',
    legal_name: '',
    inn: '',
    phone: '',
    address: '',
    director_name: '',
    director_phone: '',
    director_passport: '',
    accountant_name: '',
    accountant_phone: '',
    accountant_passport: '',
    bank_name: '',
    bank_code: '',
    bank_account: '',
    latitude: '',
    longitude: '',
    social_instagram: '',
    social_telegram: '',
    social_facebook: '',
    website: '',
  });

  const [workingHours, setWorkingHours] = useState<Record<string, string>>({
    mon: '09:00-18:00', tue: '09:00-18:00', wed: '09:00-18:00',
    thu: '09:00-18:00', fri: '09:00-18:00', sat: '09:00-15:00', sun: '',
  });

  const set = (field: string, value: string) => setForm(prev => ({ ...prev, [field]: value }));
  const setWh = (day: string, value: string) => setWorkingHours(prev => ({ ...prev, [day]: value }));

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.legal_name.trim() || !form.inn.trim()) {
      toast.error(t('adm_merchantCreate.required'));
      return;
    }
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        legal_name: form.legal_name.trim(),
        inn: form.inn.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        director_name: form.director_name.trim(),
        director_phone: form.director_phone.trim(),
        director_passport: form.director_passport.trim(),
        accountant_name: form.accountant_name.trim(),
        accountant_phone: form.accountant_phone.trim(),
        accountant_passport: form.accountant_passport.trim(),
        bank_name: form.bank_name.trim(),
        bank_code: form.bank_code.trim(),
        bank_account: form.bank_account.trim(),
        social_instagram: form.social_instagram.trim(),
        social_telegram: form.social_telegram.trim(),
        social_facebook: form.social_facebook.trim(),
        website: form.website.trim(),
        working_hours: workingHours,
      };

      if (form.latitude) body.latitude = parseFloat(form.latitude);
      if (form.longitude) body.longitude = parseFloat(form.longitude);

      const { data, error } = await apiClient.POST('/admin/merchants', { body: body as never });

      if (error) throw error;
      toast.success(t('adm_merchantCreate.created'));
      navigate(`/admin/merchants/${data.data.id}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, t('adm_merchantCreate.errorOccurred')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/merchants')}>
          <ArrowLeft size={18} />
        </Button>
        <div>
          <h1 className="text-xl font-bold text-foreground">{t('adm_merchantCreate.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('adm_merchantCreate.subtitle')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Building2 size={16} /> {t('adm_merchantCreate.companyInfo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{t('adm_merchantCreate.name')}</Label>
              <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder={t('adm_merchantCreate.namePlaceholder')} />
            </div>
            <div className="space-y-2">
              <Label>{t('adm_merchantCreate.legalName')}</Label>
              <Input value={form.legal_name} onChange={e => set('legal_name', e.target.value)} placeholder="OOO Raqamli Savdo" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>INN *</Label>
                <Input value={form.inn} onChange={e => set('inn', e.target.value)} placeholder="123456789" />
              </div>
              <div className="space-y-2">
                <Label>{t('adm_merchants.phone')}</Label>
                <Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+998..." />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('adm_merchantCreate.address')}</Label>
              <Input value={form.address} onChange={e => set('address', e.target.value)} placeholder={t('adm_merchantCreate.addressPlaceholder')} />
            </div>
          </CardContent>
        </Card>

        {/* Director & Accountant */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><User size={16} /> {t('adm_merchantCreate.management')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-muted-foreground font-medium">{t('adm_merchantCreate.director')}</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('adm_merchantCreate.fio')}</Label>
                <Input value={form.director_name} onChange={e => set('director_name', e.target.value)} placeholder={t('adm_merchantCreate.fullNamePlaceholder')} />
              </div>
              <div className="space-y-2">
                <Label>{t('adm_merchants.phone')}</Label>
                <Input value={form.director_phone} onChange={e => set('director_phone', e.target.value)} placeholder="+998..." />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('adm_merchantCreate.passport')}</Label>
              <Input value={form.director_passport} onChange={e => set('director_passport', e.target.value)} placeholder="AA1234567" />
            </div>

            <div className="border-t border-border pt-4" />
            <p className="text-xs text-muted-foreground font-medium">{t('adm_merchantCreate.accountant')}</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('adm_merchantCreate.fio')}</Label>
                <Input value={form.accountant_name} onChange={e => set('accountant_name', e.target.value)} placeholder={t('adm_merchantCreate.fullNamePlaceholder')} />
              </div>
              <div className="space-y-2">
                <Label>{t('adm_merchants.phone')}</Label>
                <Input value={form.accountant_phone} onChange={e => set('accountant_phone', e.target.value)} placeholder="+998..." />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('adm_merchantCreate.passport')}</Label>
              <Input value={form.accountant_passport} onChange={e => set('accountant_passport', e.target.value)} placeholder="AA1234567" />
            </div>
          </CardContent>
        </Card>

        {/* Bank info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Landmark size={16} /> {t('adm_merchantCreate.bankDetails')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('adm_merchantCreate.bankName')}</Label>
                <Input value={form.bank_name} onChange={e => set('bank_name', e.target.value)} placeholder="Ipak Yo'li bank" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t('adm_merchantCreate.bankCode')}</Label>
                  <Input value={form.bank_code} onChange={e => set('bank_code', e.target.value)} placeholder="00000" />
                </div>
                <div className="space-y-2">
                  <Label>{t('adm_merchantCreate.bankAccount')}</Label>
                  <Input value={form.bank_account} onChange={e => set('bank_account', e.target.value)} placeholder="20208000..." />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Location */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><MapPin size={16} /> {t('adm_merchantCreate.location')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-muted-foreground">{t('adm_merchantCreate.locationHint')}</p>
            <LocationPicker
              latitude={form.latitude ? parseFloat(form.latitude) : null}
              longitude={form.longitude ? parseFloat(form.longitude) : null}
              onChange={(lat, lng) => {
                set('latitude', lat.toString());
                set('longitude', lng.toString());
              }}
            />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Latitude</Label>
                <Input value={form.latitude} onChange={e => set('latitude', e.target.value)} placeholder="41.2995" type="number" step="any" />
              </div>
              <div className="space-y-2">
                <Label>Longitude</Label>
                <Input value={form.longitude} onChange={e => set('longitude', e.target.value)} placeholder="69.2401" type="number" step="any" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Working hours */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Clock size={16} /> {t('adm_merchantCreate.workingHours')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {getDays(t).map(d => (
              <div key={d.key} className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground w-24">{d.label}</span>
                <Input
                  value={workingHours[d.key]}
                  onChange={e => setWh(d.key, e.target.value)}
                  placeholder={t('adm_merchantCreate.hoursPlaceholder')}
                  className="flex-1"
                />
              </div>
            ))}
            <p className="text-xs text-muted-foreground">{t('adm_merchantCreate.hoursHint')}</p>
          </CardContent>
        </Card>

        {/* Social links */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Share2 size={16} /> {t('adm_merchantCreate.social')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Instagram</Label>
              <Input value={form.social_instagram} onChange={e => set('social_instagram', e.target.value)} placeholder="@dokon_uz" />
            </div>
            <div className="space-y-2">
              <Label>Telegram</Label>
              <Input value={form.social_telegram} onChange={e => set('social_telegram', e.target.value)} placeholder="@dokon_bot" />
            </div>
            <div className="space-y-2">
              <Label>Facebook</Label>
              <Input value={form.social_facebook} onChange={e => set('social_facebook', e.target.value)} placeholder="facebook.com/dokon" />
            </div>
            <div className="space-y-2">
              <Label>{t('adm_merchantCreate.website')}</Label>
              <Input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://dokon.uz" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/admin/merchants')}>{t('adm_common.cancel')}</Button>
        <Button onClick={handleSubmit} disabled={saving}>
          <Save size={16} className="mr-2" />
          {saving ? t('adm_common.saving') : t('adm_merchantCreate.submit')}
        </Button>
      </div>
    </div>
  );
}
